import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { Toaster } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const CampaignDetail = lazy(() => import("@/pages/CampaignDetail"));
const CreatorDashboard = lazy(() => import("@/pages/CreatorDashboard"));
const BrandDashboard = lazy(() => import("@/pages/BrandDashboard"));
const Withdraw = lazy(() => import("@/pages/Withdraw"));
const Profile = lazy(() => import("@/pages/Profile"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const AdminWithdrawals = lazy(() => import("@/pages/AdminWithdrawals"));
const AdminMembers = lazy(() => import("@/pages/AdminMembers"));
const AdminCampaigns = lazy(() => import("@/pages/AdminCampaigns"));
const AdminPaymentApprovals = lazy(() => import("@/pages/AdminPaymentApprovals"));
const AdminCampaignBlasts = lazy(() => import("@/pages/AdminCampaignBlasts"));
const AdminSupport = lazy(() => import("@/pages/AdminSupport"));
const Support = lazy(() => import("@/pages/Support"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

const MEMBER_CHAT_ENABLED = process.env.REACT_APP_MEMBER_CHAT_ENABLED === "true";
const MemberChatDock = MEMBER_CHAT_ENABLED
  ? lazy(() => import("@/components/MemberChatDock"))
  : null;

function dashboardFor(user) {
  if (user?.role === "admin") return "/admin";
  if (user?.role === "brand" || user?.role === "client") return "/client";
  return "/dashboard";
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="p-12 text-center">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && !(user.role === role || (role === "brand" && user.role === "client") || (role === "client" && user.role === "brand"))) return <Navigate to={dashboardFor(user)} replace />;
  return children;
}

function Shell() {
  const { user } = useAuth();
  return (
    <div className="App">
      <Header />
      <Suspense fallback={<div className="p-12 text-center">Memuat halaman...</div>}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/:id" element={<CampaignDetail />} />
          <Route path="/cara-kerja" element={<HowItWorks />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/dashboard" element={<Protected role="creator"><CreatorDashboard /></Protected>} />
          <Route path="/brand" element={<Protected role="brand"><BrandDashboard /></Protected>} />
          <Route path="/client" element={<Protected role="client"><BrandDashboard /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><AdminWithdrawals /></Protected>} />
          <Route path="/admin/members" element={<Protected role="admin"><AdminMembers /></Protected>} />
          <Route path="/admin/submissions" element={<Protected role="admin"><Navigate to="/admin/campaigns" replace /></Protected>} />
          <Route path="/admin/campaigns" element={<Protected role="admin"><AdminCampaigns /></Protected>} />
          <Route path="/admin/payments" element={<Protected role="admin"><AdminPaymentApprovals /></Protected>} />
          <Route path="/admin/blasts" element={<Protected role="admin"><AdminCampaignBlasts /></Protected>} />
          <Route path="/admin/support" element={<Protected role="admin"><AdminSupport /></Protected>} />
          <Route path="/support" element={<Protected><Support /></Protected>} />
          <Route path="/withdraw" element={<Protected role="creator"><Withdraw /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
        </Routes>
      </Suspense>
      {MEMBER_CHAT_ENABLED && MemberChatDock && user && user !== false && (
        <Suspense fallback={null}>
          <MemberChatDock />
        </Suspense>
      )}
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <Shell />
          <Toaster position="top-right" richColors closeButton />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
